import styled from '@emotion/styled';
import {
  Pill,
  Skeleton,
  SvgIcon,
  SvgNames,
  tokens,
} from '@sunrun/experience-ui-components';
import type { WorkdayEventType } from 'constants/workday';
import { getWorkHoursStatusPillConfig } from 'helpers/workdayPillHelpers';
import type { RecentEventStatus } from 'hooks/useRecentTimeClockEvent';

type WorkHoursStatusPillProps = {
  status: RecentEventStatus;
  eventType: WorkdayEventType | null;
  timeEntryCodeDescriptor?: string;
  isRefreshing?: boolean;
  isMobile?: boolean;
};

const WorkHoursStatusPill = ({
  status,
  eventType,
  timeEntryCodeDescriptor,
  isRefreshing = false,
  isMobile = false,
}: WorkHoursStatusPillProps) => {
  if (status === 'error') {
    return null;
  }

  if (isRefreshing) {
    return (
      <RefreshingSlot
        data-testid="work-hours-status-pill-refreshing"
        $isMobile={isMobile}>
        <SvgIcon
          name={SvgNames.Loading}
          width={20}
          height={20}
          color={tokens.BLACK}
        />
      </RefreshingSlot>
    );
  }

  if (status === 'loading') {
    return (
      <LoadingPillContainer
        data-testid="work-hours-status-pill-loading"
        $isMobile={isMobile}>
        <LoadingSkeleton />
      </LoadingPillContainer>
    );
  }

  const pillConfig = getWorkHoursStatusPillConfig(
    eventType,
    timeEntryCodeDescriptor,
  );

  if (!pillConfig) {
    return null;
  }

  return (
    <StatusPill
      data-testid="work-hours-status-pill"
      text={pillConfig.text}
      type={pillConfig.type}
      icon={pillConfig.icon}
      $isMobile={isMobile}
    />
  );
};

const RefreshingSlot = styled.div<{ $isMobile: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 30px;
  min-width: 30px;
  width: fit-content;

  ${({ $isMobile }) =>
    $isMobile &&
    `
    width: 100%;
  `}
`;

const LoadingSkeleton = styled(Skeleton)`
  width: 160px;
  height: 14px;
  min-height: 14px;
`;

const LoadingPillContainer = styled.div<{ $isMobile: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 30px;
  padding: 7px 16px;
  box-sizing: border-box;
  border-radius: 6px;
  border: 1px solid ${tokens.SUCCESS_70};
  background: ${tokens.SUCCESS_90};
  width: fit-content;

  ${({ $isMobile }) =>
    $isMobile &&
    `
    width: 100%;
  `}
`;

const StatusPill = styled(Pill)<{ $isMobile: boolean }>`
  padding: 2px 12px !important;
  white-space: nowrap;

  ${({ $isMobile }) =>
    $isMobile &&
    `
    width: 100%;
  `}
`;

export { WorkHoursStatusPill };
